import { useMemo, useRef, useState } from 'react';
import {
  apiErrorText,
  useCreateCeoInflowPlanMutation,
  useGetCeoInflowsQuery,
  useUpdateCeoInflowFactMutation,
  type CeoInflowPlanRow,
} from '@shared/api';
import { todayIso } from '@shared/lib/localDate';
import { moneyInput, parseMoney } from '@shared/lib/money';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { TreasuryViewNote, useTreasuryWrite } from '../treasuryAccess';
import { HeaderActions } from '../ui';
import { AddInflowPlanModal } from './AddInflowPlanModal';
import { formatPeriodMonthLabel } from './inflowHelpers';
import { InflowsFunnelGrid } from './InflowsFunnelGrid';
import { InflowsTable } from './InflowsTable';
import {
  canSave,
  checkFactDraft,
  closeEditor,
  defaultExpectedDate,
  editDraft,
  finishSaving,
  openEditor,
  recentPeriods,
  showFieldErrors,
  startSaving,
  type FactDraft,
  type PlanPayload,
  type RowEditors,
} from './model';
import { SmetaKs3ActsTable } from './SmetaKs3ActsTable';
import {
  ErrorRow,
  HeaderRow,
  Section,
  Select,
  SubTitleText,
  Title,
} from './styles';
import type { InflowsPageProps } from './types';

export function CeoInflowsView({ editable = false }: InflowsPageProps) {
  const can_write = useTreasuryWrite(editable);
  const [periodFilter, setPeriodFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const { data, isLoading, isError, refetch } = useGetCeoInflowsQuery(
    periodFilter || statusFilter
      ? {
          period_month: periodFilter || undefined,
          status: statusFilter || undefined,
        }
      : undefined,
  );
  const [createInflow, { isLoading: isCreating }] = useCreateCeoInflowPlanMutation();
  const [updateFact] = useUpdateCeoInflowFactMutation();

  const [showAddForm, setShowAddForm] = useState(false);
  // Редакторы факта по строкам: у каждой свой черновик, ожидание и ошибка
  const [editors, setEditors] = useState<RowEditors>({});
  // Строки, ответа по которым ждём, — чтобы двойной щелчок не отправил дважды
  const inFlight = useRef(new Set<string>());

  const availablePeriods = useMemo(() => {
    const set = new Set<string>(recentPeriods(new Date()));
    if (periodFilter) {
      set.add(periodFilter.slice(0, 10));
    }
    for (const r of data?.rows ?? []) {
      if (r.period_month) {
        set.add(r.period_month.slice(0, 10));
      }
    }
    return Array.from(set).sort().reverse();
  }, [data?.rows, periodFilter]);

  if (isLoading) {
    return <Card>Загрузка аналитики поступлений БДДС / КС-3 / Факт…</Card>;
  }

  if (isError || !data) {
    return (
      <Card>
        <ErrorRow>
          <span>Не удалось загрузить аналитику поступлений БДДС / КС-3 / Факт.</span>
          <Button $size="sm" $variant="secondary" onClick={() => refetch()}>
            Повторить
          </Button>
        </ErrorRow>
      </Card>
    );
  }

  // Форма закрывается только после подтверждённого сохранения; ошибку показывает она сама
  async function handleCreate(payload: PlanPayload) {
    await createInflow(payload).unwrap();
    setShowAddForm(false);
  }

  async function handleSaveRowFact(row: CeoInflowPlanRow) {
    const editor = editors[row.id];
    if (!editor || !canSave(editors, row.id) || inFlight.current.has(row.id)) return;
    const { errors, payload } = checkFactDraft(editor.draft, row, parseMoney);
    if (!payload) {
      setEditors((e) => showFieldErrors(e, row.id, errors));
      return;
    }
    if (Object.keys(payload).length === 0) {
      setEditors((e) => closeEditor(e, row.id));
      return;
    }
    inFlight.current.add(row.id);
    setEditors((e) => startSaving(e, row.id));
    try {
      await updateFact({ id: row.id, ...payload }).unwrap();
      setEditors((e) => finishSaving(e, row.id, null));
    } catch (err) {
      setEditors((e) =>
        finishSaving(e, row.id, apiErrorText(err, 'Не удалось сохранить факт по строке.')),
      );
    } finally {
      inFlight.current.delete(row.id);
    }
  }

  function handleStartEdit(row: CeoInflowPlanRow) {
    setEditors((e) =>
      openEditor(e, row.id, {
        ks3: moneyInput(row.ks3_signed_amount),
        ks3DocNo: row.ks3_doc_number ?? '',
        actual: moneyInput(row.actual_received_amount),
      }),
    );
  }

  function handleDraftChange(rowId: string, patch: Partial<FactDraft>) {
    setEditors((e) => editDraft(e, rowId, patch));
  }

  return (
    <>
      {!editable && <TreasuryViewNote section="Поступления (КС-3)" to="/agents/finance/inflows" />}
      <Section>
        <HeaderRow>
          <div>
            <Title>
              Планируемые поступления по БДДС vs Ожидаемые по подписанным КС-3 vs Фактически
              поступившие
            </Title>
            <SubTitleText>
              Сквозная сверка трёх контуров денежных притоков: казначейский план БДДС, подписанные с
              Заказчиком справки о стоимости выполненных работ КС-3 (за вычетом аванса и ГУ) и
              фактический приход денег на р/с.
            </SubTitleText>
          </div>

          <HeaderActions>
            <Select
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
              aria-label="Период БДДС"
            >
              <option value="">Все периоды БДДС</option>
              {availablePeriods.map((pm) => (
                <option key={pm} value={pm}>
                  {formatPeriodMonthLabel(pm)}
                </option>
              ))}
            </Select>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Статус поступления"
            >
              <option value="">Все статусы поступлений</option>
              <option value="bdds_forecast">План БДДС (ожидается КС-2)</option>
              <option value="ks2_on_review">КС-2 на проверке у Заказчика</option>
              <option value="ks3_signed_awaiting">КС-3 подписана — ожидаем оплату</option>
              <option value="partially_paid">Частично оплачено по КС-3</option>
              <option value="overdue_receivable">Просрочка оплаты по КС-3</option>
              <option value="paid_in_full">Оплачено в полном объёме</option>
            </Select>
            {can_write && (
              <Button $size="sm" onClick={() => setShowAddForm((v) => !v)}>
                + Добавить план БДДС / КС-3
              </Button>
            )}
          </HeaderActions>
        </HeaderRow>

        {can_write && showAddForm && (
          <AddInflowPlanModal
            defaultExpectedDate={defaultExpectedDate(periodFilter, todayIso())}
            isCreating={isCreating}
            onCreate={handleCreate}
            onClose={() => setShowAddForm(false)}
          />
        )}

        <InflowsFunnelGrid
          totalBddsPlanned={data.total_bdds_planned}
          totalKs2Submitted={data.total_ks2_submitted}
          bddsToKs3Gap={data.bdds_to_ks3_gap}
          totalExpectedNetKs3={data.total_expected_net_ks3}
          totalKs3SignedGross={data.total_ks3_signed_gross}
          totalAdvanceDeduction={data.total_advance_deduction}
          totalRetentionDeduction={data.total_retention_deduction}
          totalActualReceived={data.total_actual_received}
          bddsFulfillmentPct={data.bdds_fulfillment_pct}
          ks3CollectionPct={data.ks3_collection_pct}
          ks3AwaitingCash={data.ks3_awaiting_cash}
          overdueKs3Receivables={data.overdue_ks3_receivables}
        />
      </Section>

      <InflowsTable
        rows={data.rows}
        editors={editors}
        onDraftChange={handleDraftChange}
        can_write={can_write}
        onStartEdit={handleStartEdit}
        onCancelEdit={(rowId) => setEditors((e) => closeEditor(e, rowId))}
        onSaveRowFact={handleSaveRowFact}
      />

      <SmetaKs3ActsTable acts={data.smeta_ks3_acts} />
    </>
  );
}
